package pages

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import shared.config.WebsiteCOs
import shared.models.LegalSection

@Composable
fun LegalPageLayout(
    title: String,
    content: @Composable () -> Unit
) {
    Div(attrs = {
        style {
            maxWidth(56.cssRem)
            property("margin", "0 auto")
            property("padding", "4rem 1.5rem")
        }
    }) {
        H1(attrs = {
            style {
                fontSize(2.25.cssRem)
                lineHeight(2.5.cssRem)
                fontWeight(700)
                marginBottom(2.cssRem)
                property("color", "var(--on-surface)")
            }
        }) { Text(title) }

        Div(attrs = {
            style {
                padding(2.cssRem)
                borderRadius(0.5.cssRem)
                property("border", "1px solid var(--outline-variant)")
                property("background-color", "var(--surface-container)")
            }
        }) {
            Div(attrs = {
                style {
                    property("max-width", "none")
                    property("color", "var(--on-surface)")
                }
            }) {
                content()
            }
        }
    }
}

@Composable
fun LegalSectionView(section: LegalSection, depth: Int = 0) {
    if (section.heading.isNotEmpty()) {
        when (depth) {
            0 -> H2(attrs = {
                style {
                    fontSize(1.5.cssRem)
                    lineHeight(2.cssRem)
                    fontWeight(700)
                    marginTop(2.cssRem)
                    marginBottom(1.cssRem)
                    property("color", "var(--primary)")
                }
            }) { Text(section.heading) }
            1 -> H3(attrs = {
                style {
                    fontSize(1.25.cssRem)
                    lineHeight(1.75.cssRem)
                    fontWeight(700)
                    marginTop(1.5.cssRem)
                    marginBottom(0.75.cssRem)
                    property("color", "var(--primary)")
                }
            }) { Text(section.heading) }
            else -> H4(attrs = {
                style {
                    fontSize(1.1.cssRem)
                    lineHeight(1.5.cssRem)
                    fontWeight(700)
                    marginTop(1.25.cssRem)
                    marginBottom(0.5.cssRem)
                    property("color", "var(--primary)")
                }
            }) { Text(section.heading) }
        }
    }

    if (section.body.isNotEmpty()) {
        section.body.split("\n\n").forEach { paragraph ->
            P(attrs = { style { marginBottom(1.cssRem) } }) {
                RichLegalText(paragraph)
            }
        }
    }

    if (section.bulletPoints.isNotEmpty()) {
        Ul(attrs = {
            style {
                property("list-style-type", "disc")
                property("padding-left", "1.5rem")
                marginBottom(1.cssRem)
            }
        }) {
            section.bulletPoints.forEach { point ->
                Li(attrs = { style { marginBottom(0.5.cssRem) } }) {
                    RichLegalText(point)
                }
            }
        }
    }

    section.subSections.forEach { sub ->
        LegalSectionView(sub, depth + 1)
    }
}

@Composable
private fun RichLegalText(text: String) {
    val email = WebsiteCOs.contactEmail
    val boldPattern = Regex("\\*\\*(.+?)\\*\\*")
    val matches = boldPattern.findAll(text).toList()

    if (matches.isEmpty()) {
        renderTextWithEmail(text, email)
        return
    }

    var lastIndex = 0
    matches.forEach { match ->
        if (match.range.first > lastIndex) {
            renderTextWithEmail(text.substring(lastIndex, match.range.first), email)
        }
        Span(attrs = { style { fontWeight(700) } }) {
            Text(match.groupValues[1])
        }
        lastIndex = match.range.last + 1
    }
    if (lastIndex < text.length) {
        renderTextWithEmail(text.substring(lastIndex), email)
    }
}

@Composable
private fun renderTextWithEmail(text: String, email: String) {
    if (email in text) {
        val idx = text.indexOf(email)
        Text(text.substring(0, idx))
        A(attrs = {
            attr("href", "mailto:$email")
            style {
                textDecoration("underline")
                property("color", "var(--primary)")
            }
        }) { Text(email) }
        Text(text.substring(idx + email.length))
    } else {
        Text(text)
    }
}
